<template>
  <el-dialog
    :title="dialogTitle"
    :visible.sync="dialogVisible"
    width="800px"
    :close-on-click-modal="false"
    :before-close="handleDialogClose"
  >
    <!-- 5步步骤条 -->
    <el-steps
      :active="activeStep"
      finish-status="success"
      style="margin-bottom: 25px"
    >
      <el-step title="选择合同模板"></el-step>
      <el-step title="选择负责人与房屋"></el-step>
      <!-- 步骤2 修改为：填写乙方信息 -->
      <el-step title="填写乙方信息"></el-step>
      <el-step title="房屋与合同信息"></el-step>
      <el-step title="租赁与付款信息"></el-step>
      <el-step title="政策信息"></el-step>
    </el-steps>

    <!-- 步骤内容区域 -->
    <el-form
      ref="contractForm"
      :model="formData"
      :rules="formRules"
      label-width="160px"
      class="contract-form"
    >
      <!-- 步骤0：选择合同模板 -->
      <template v-if="activeStep === 0">
        <el-form-item
          label="合同模板"
          prop="templateType"
          class="required-item"
        >
          <!-- 添加唯一Key，避免与房屋选择下拉框复用 -->
          <el-select
            v-model="formData.templateType"
            placeholder="请选择合同模板"
            @change="handleTemplateChange"
            style="width: 100%"
            key="template-select"
          >
            <el-option
              label="房屋租赁协议模板（装修抵租金.不含采暖费版本）"
              value="houseRent"
            ></el-option>
            <el-option
              label="光谷房屋租赁协议最新版本"
              value="houseRentNew"
            ></el-option>
            <el-option
              label="房屋租赁协议模板（终版.含采暖费）"
              value="houseRentHeating"
            ></el-option>
            <el-option
              label="房屋租赁协议模板（终版.不含采暖费版本）"
              value="houseRentNotHeating"
            ></el-option>
          </el-select>
        </el-form-item>
        <el-card v-if="formData.templateType" class="template-desc">
          <div slot="header">模板说明</div>
          <p>{{ getTemplateDesc() }}</p>
          <div
            class="template-tips"
            v-if="formData.templateType === 'houseRent'"
          >
            <i class="el-icon-info"></i>
            提示：该模板需额外填写装修金额、投入金额等字段
          </div>
          <div
            class="template-tips"
            v-if="formData.templateType === 'houseRentNew'"
          >
            <i class="el-icon-info"></i>
            提示：该模板适用于工业用地/厂房租赁，需填写甲方邮寄地址
          </div>
        </el-card>
      </template>

      <!-- 步骤1：选择负责人与房屋 -->
      <template v-if="activeStep === 1">
        <!-- 合同负责人 -->
        <el-form-item
          label="合同负责人"
          prop="contractManager"
          class="required-item"
        >
          <el-select
            v-model="formData.contractManager"
            :placeholder="
              loadingStaff
                ? '加载中...'
                : staffList.length === 0
                ? '未查询到负责人'
                : '请选择合同负责人'
            "
            style="width: 100%"
            :disabled="loadingStaff || staffList.length === 0"
          >
            <el-option
              v-for="staff in staffList"
              :key="staff.userId"
              :label="staff.name"
              :value="staff.userId"
            ></el-option>
          </el-select>
        </el-form-item>

        <!-- 选择房屋 - 改造为远程搜索模式 -->
        <el-form-item
          label="选择房屋"
          prop="selectedHouse"
          class="required-item"
        >
          <el-select
            ref="houseSelect"
            v-model="formData.selectedHouse"
            multiple
            collapse-tags
            filterable
            remote
            reserve-keyword
            :remote-method="searchRoomList"
            :loading="loadingRooms"
            :placeholder="
              loadingRooms
                ? '加载中...'
                : filteredRoomList.length === 0
                ? '未查询到房屋数据'
                : '请输入房号/名称搜索并选择'
            "
            style="width: 100%"
            :disabled="loadingRooms"
            @change="handleHouseChange"
            @focus="onHouseSelectFocus"
            key="house-select-remote-search"
          >
            <!-- 有效房屋选项 -->
            <el-option
              v-for="room in filteredRoomList"
              :key="`room-${room.roomId}`"
              :label="`${room.roomName || '未知房屋'}（${
                room.builtUpArea || 0
              }㎡）`"
              :value="room.roomId"
            ></el-option>
            <!-- 无数据提示 -->
            <div
              v-if="!loadingRooms && filteredRoomList.length === 0"
              class="empty-option"
              style="padding: 10px 0; text-align: center; color: #999"
            >
              {{ searchQuery ? "未找到匹配的房屋" : "未查询到房屋数据" }}
            </div>
          </el-select>
          <!-- 调试信息 -->
          <div
            style="
              font-size: 12px;
              color: #666;
              margin-top: 6px;
              display: flex;
              gap: 15px;
            "
          >
            <span>房屋列表数量：{{ roomList.length }} 条</span>
            <span>已选房屋数量：{{ formData.selectedHouse.length }} 个</span>
          </div>
        </el-form-item>

        <!-- 已选房屋详情 -->
        <el-card
          v-if="formData.selectedHouse.length > 0"
          class="selected-house-card"
        >
          <div slot="header">已选房屋详情</div>
          <el-table
            ref="houseTable"
            :data="getSelectedHouseDetails()"
            border
            style="width: 100%"
            :key="houseAmountUpdated"
          >
            <el-table-column
              prop="roomName"
              label="房屋名称"
              align="center"
            ></el-table-column>
            <el-table-column
              prop="builtUpArea"
              label="建筑面积(㎡)"
              align="center"
            ></el-table-column>
            <el-table-column
              prop="roomSubTypeName"
              label="房屋类型"
              align="center"
            ></el-table-column>
            <el-table-column
              prop="stateName"
              label="房屋状态"
              align="center"
            ></el-table-column>
            <el-table-column
              prop="rentAmount"
              label="单套租金(元)"
              align="center"
            ></el-table-column>
            <!-- 新增采暖费列 -->
            <el-table-column
              v-if="
                ['houseRent', 'houseRentNotHeating'].includes(
                  formData.templateType,
                )
              "
              prop="heatingAmount"
              label="单套采暖费(元)"
              align="center"
            ></el-table-column>
          </el-table>
          <div
            class="selected-house-summary"
            style="margin-top: 10px; text-align: right"
          >
            <span style="margin-right: 20px"
              >总面积：{{ formData.houseArea || 0 }} ㎡</span
            >
            <template
              v-if="
                ['houseRent', 'houseRentNotHeating'].includes(
                  formData.templateType,
                )
              "
            >
              <span style="margin-right: 20px"
                >总租金：{{ totalRoomAmount || 0 }} 元</span
              >
              <span style="margin-right: 20px"
                >总采暖费：{{ formData.heatingFee || 0 }} 元</span
              >
              <span
                >合同总金额：{{
                  (totalRoomAmount + (formData.heatingFee || 0)).toFixed(2)
                }}
                元</span
              >
            </template>
            <template v-else>
              <span>总金额：{{ totalRoomAmount || 0 }} 元</span>
            </template>
          </div>
        </el-card>
      </template>

      <!-- 步骤2（原步骤3）：填写乙方信息 -->
      <template v-if="activeStep === 2">
        <el-form-item label="乙方公司全称" prop="partyBName">
          <el-input
            v-model="formData.partyBName"
            placeholder="请输入乙方公司全称"
            maxlength="100"
            show-word-limit
          ></el-input>
        </el-form-item>
        <el-form-item label="乙方注册地址" prop="partyBAddress">
          <el-input
            v-model="formData.partyBAddress"
            placeholder="请输入乙方公司注册地址"
            maxlength="200"
            show-word-limit
          ></el-input>
        </el-form-item>
        <el-form-item label="乙方联系人" prop="partyBContact">
          <el-input
            v-model="formData.partyBContact"
            placeholder="请输入乙方联系人姓名"
            maxlength="20"
            show-word-limit
          ></el-input>
        </el-form-item>
        <el-form-item label="乙方联系电话" prop="partyBPhone">
          <el-input
            v-model="formData.partyBPhone"
            placeholder="请输入乙方联系电话"
            maxlength="20"
            show-word-limit
          ></el-input>
        </el-form-item>
        <el-form-item label="乙方邮箱" prop="partyBEmail">
          <el-input
            v-model="formData.partyBEmail"
            placeholder="请输入乙方邮箱"
            maxlength="50"
            show-word-limit
          ></el-input>
        </el-form-item>
        <!-- 乙方邮寄地址改为非必填 -->
        <el-form-item label="乙方邮寄地址" prop="partyBmAilingAddress">
          <el-input
            v-model="formData.partyBmAilingAddress"
            placeholder="请输入乙方邮寄地址"
            maxlength="200"
            show-word-limit
          ></el-input>
        </el-form-item>
        <el-form-item label="乙方法定代表人" prop="partyBLegal">
          <el-input
            v-model="formData.partyBLegal"
            placeholder="请输入乙方法定代表人姓名"
            maxlength="20"
            show-word-limit
          ></el-input>
        </el-form-item>
      </template>

      <!-- 步骤3（原步骤4）：房屋与合同信息 -->
      <template v-if="activeStep === 3">
        <!-- 采暖费输入 - 仅特定模板显示 -->
        <el-form-item
          v-if="
            ['houseRent', 'houseRentNotHeating'].includes(formData.templateType)
          "
          label="采暖费（元）"
          prop="heatingFee"
          class="required-item"
        >
          <el-input
            v-model.number="formData.heatingFee"
            placeholder="请输入采暖费金额"
            type="number"
            min="0"
            step="0.01"
            @change="calcTotalWithHeatingFee"
          ></el-input>
        </el-form-item>

        <el-form-item label="租金（大写）" prop="rent">
          <el-input
            v-model="formData.rent"
            placeholder="请输入租金（中文大写）"
            maxlength="50"
            show-word-limit
            :readonly="formData.selectedHouse.length > 0"
            tooltip="已根据选中房屋自动计算，可手动修改"
            @change="handleRentChange"
          ></el-input>
        </el-form-item>
        <el-form-item label="房屋详细地址" prop="houseAddress">
          <el-input
            v-model="formData.houseAddress"
            placeholder="例：X号楼第XXX层XXX号房屋"
            maxlength="200"
            show-word-limit
            tooltip="已根据选中房屋自动拼接，可手动修改"
          ></el-input>
        </el-form-item>
        <el-form-item label="合同签订日期" prop="signDate">
          <el-date-picker
            v-model="formData.signDate"
            type="date"
            placeholder="选择合同签订日期"
            format="yyyy年MM月dd日"
            value-format="yyyy-MM-dd"
            style="width: 100%"
          ></el-date-picker>
        </el-form-item>
        <el-form-item label="房屋建筑面积(㎡)" prop="houseArea">
          <el-input
            v-model.number="formData.houseArea"
            placeholder="请输入房屋建筑面积"
            type="number"
            min="0"
            step="0.01"
            :readonly="formData.selectedHouse.length > 0"
            tooltip="已根据选中房屋自动计算，可手动修改"
          ></el-input>
        </el-form-item>
        <el-form-item label="房屋用途" prop="buildingUsage">
          <el-select
            v-model="formData.buildingUsage"
            placeholder="请选择房屋用途"
            style="width: 100%"
          >
            <template v-if="formData.templateType === 'houseRentNew'">
              <el-option label="工业用地" value="工业用地"></el-option>
              <el-option label="厂房" value="厂房"></el-option>
            </template>
            <template v-else>
              <el-option label="办公" value="办公"></el-option>
              <el-option label="商业" value="商业"></el-option>
            </template>
          </el-select>
        </el-form-item>
        <template v-if="formData.templateType === 'houseRent'">
          <el-form-item label="装修金额(万元)" prop="decorationAmount">
            <el-input
              v-model.number="formData.decorationAmount"
              placeholder="请输入装修金额"
              type="number"
              min="0"
              step="0.01"
            ></el-input>
          </el-form-item>
          <el-form-item label="投入金额(万元)" prop="investmentAmount">
            <el-input
              v-model.number="formData.investmentAmount"
              placeholder="请输入投入金额"
              type="number"
              min="0"
              step="0.01"
            ></el-input>
          </el-form-item>
          <el-form-item label="保证金金额(万元)" prop="depositAmount">
            <el-input
              v-model.number="formData.depositAmount"
              placeholder="请输入保证金金额"
              type="number"
              min="0"
            ></el-input>
          </el-form-item>
        </template>
      </template>

      <!-- 步骤4（原步骤5）：租赁与付款信息 -->
      <template v-if="activeStep === 4">
        <el-form-item label="保证金" prop="marginDeposit">
          <el-input
            v-model.number="formData.marginDeposit"
            placeholder="请输入保证金金额"
            type="number"
            min="0"
          ></el-input>
        </el-form-item>
        <el-form-item label="孵化周期(年)" prop="incubationPeriod">
          <el-input
            v-model.number="formData.incubationPeriod"
            placeholder="请输入孵化周期"
            type="number"
            min="1"
            @change="handleIncubationPeriodChange"
          ></el-input>
        </el-form-item>
        <!-- 孵化周期时间范围改为时间选择器 -->
        <el-form-item label="孵化周期时间范围" prop="incubationPeriodTimeRange">
          <el-date-picker
            v-model="formData.incubationPeriodTimeRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            format="yyyy年MM月dd日"
            value-format="yyyy-MM-dd"
            style="width: 100%"
            @change="updateIncubationPeriodText"
          ></el-date-picker>
        </el-form-item>
        <!-- 房屋装修期改为时间选择器 -->
        <el-form-item label="房屋装修期" prop="houseDecorationPeriodRange">
          <el-date-picker
            v-model="formData.houseDecorationPeriodRange"
            type="daterange"
            range-separator="至"
            start-placeholder="装修开始日期"
            end-placeholder="装修结束日期"
            format="yyyy年MM月dd日"
            value-format="yyyy-MM-dd"
            style="width: 100%"
            @change="updateHouseDecorationPeriodText"
          ></el-date-picker>
        </el-form-item>
        <!-- 租赁期限改为时间选择器 -->
        <el-form-item label="租赁期限" prop="leaseTermRange">
          <el-date-picker
            v-model="formData.leaseTermRange"
            type="daterange"
            range-separator="至"
            start-placeholder="租赁开始日期"
            end-placeholder="租赁结束日期"
            format="yyyy年MM月dd日"
            value-format="yyyy-MM-dd"
            style="width: 100%"
            @change="updateLeaseTermText"
          ></el-date-picker>
        </el-form-item>

        <el-divider content-position="left">租金标准日期</el-divider>
        <el-form-item label="租金标准开始日期" prop="rentStart">
          <el-date-picker
            v-model="formData.rentStart"
            type="date"
            placeholder="选择租金标准开始日期"
            format="yyyy年MM月dd日"
            value-format="yyyy-MM-dd"
            style="width: 100%"
            @change="handleRentStartDateChange"
          ></el-date-picker>
        </el-form-item>
        <el-form-item label="租金标准截止日期" prop="rentEnd">
          <el-date-picker
            v-model="formData.rentEnd"
            type="date"
            placeholder="选择租金标准截止日期"
            format="yyyy年MM月dd日"
            value-format="yyyy-MM-dd"
            style="width: 100%"
            @change="handleRentEndDateChange"
          ></el-date-picker>
        </el-form-item>

        <!-- 租赁支付方式选择 -->
        <!-- 租赁支付方式选择 - 修复 setTimeout 错误 -->
        <el-form-item
          label="租赁支付方式"
          prop="paymentMethod"
          class="required-item"
        >
          <el-select
            v-model="formData.paymentMethod"
            placeholder="请选择租赁支付方式"
            style="width: 100%"
            @change="handlePaymentMethodChange"
          >
            <el-option label="月付" value="monthly"></el-option>
            <el-option label="季付" value="quarterly"></el-option>
            <el-option label="半年付" value="half-yearly"></el-option>
            <el-option label="年付" value="yearly"></el-option>
          </el-select>
        </el-form-item>

        <!-- 动态付款信息 -->
        <el-divider content-position="left">付款信息</el-divider>
        <div
          v-for="(payment, index) in formData.payments"
          :key="index"
          class="payment-group"
        >
          <div class="payment-group-header">
            <span class="payment-group-title">第{{ index + 1 }}笔付款信息</span>
            <el-button
              type="text"
              icon="el-icon-delete"
              @click="removePayment(index)"
              :disabled="formData.payments.length <= 1"
              class="delete-btn"
            >
              删除
            </el-button>
          </div>
          <el-form-item
            :label="`付款日期 ${index + 1}`"
            :prop="`payments[${index}].paymentDate`"
            :rules="formRules.paymentDate"
          >
            <el-date-picker
              v-model="payment.paymentDate"
              type="date"
              placeholder="选择付款日期"
              format="yyyy年MM月dd日"
              value-format="yyyy-MM-dd"
              style="width: 100%"
            ></el-date-picker>
          </el-form-item>
          <el-form-item
            :label="`租赁起始日期 ${index + 1}`"
            :prop="`payments[${index}].startDate`"
            :rules="formRules.startDate"
          >
            <el-date-picker
              v-model="payment.startDate"
              type="date"
              placeholder="选择租赁起始日期"
              format="yyyy年MM月dd日"
              value-format="yyyy-MM-dd"
              style="width: 100%"
            ></el-date-picker>
          </el-form-item>
          <el-form-item
            :label="`租赁截止日期 ${index + 1}`"
            :prop="`payments[${index}].endDate`"
            :rules="formRules.endDate"
          >
            <el-date-picker
              v-model="payment.endDate"
              type="date"
              placeholder="选择租赁截止日期"
              format="yyyy年MM月dd日"
              value-format="yyyy-MM-dd"
              style="width: 100%"
            ></el-date-picker>
          </el-form-item>
          <el-form-item
            :label="`计租天数 ${index + 1}`"
            :prop="`payments[${index}].rentalDays`"
            :rules="formRules.rentalDays"
          >
            <el-input
              v-model.number="payment.rentalDays"
              placeholder="请输入计租天数"
              type="number"
              min="1"
            ></el-input>
          </el-form-item>
          <el-form-item
            :label="`租金金额 ${index + 1}`"
            :prop="`payments[${index}].rentAmount`"
            :rules="formRules.rentAmount"
          >
            <el-input
              v-model.number="payment.rentAmount"
              placeholder="由总金额除以组数自动计算"
              type="number"
              min="0"
              :readonly="true"
              tooltip="由总金额除以组数自动计算，不可手动修改"
            ></el-input>
          </el-form-item>
          <el-divider v-if="index < formData.payments.length - 1"></el-divider>
        </div>
        <el-button
          type="primary"
          icon="el-icon-plus"
          @click="addPayment"
          style="margin-top: 10px"
        >
          添加付款信息
        </el-button>

        <el-divider content-position="left">汇总信息</el-divider>
        <div
          v-if="
            ['houseRent', 'houseRentNotHeating'].includes(formData.templateType)
          "
          style="
            margin-bottom: 10px;
            padding: 10px;
            background: #f5f5f5;
            border-radius: 4px;
          "
        >
          <div
            style="
              display: flex;
              justify-content: space-between;
              margin-bottom: 5px;
            "
          >
            <span>租金金额：</span>
            <span>{{ totalRoomAmount.toFixed(2) }} 元</span>
            <!-- 直接显示纯租金 -->
          </div>
          <div style="display: flex; justify-content: space-between">
            <span>采暖费金额：</span>
            <span>{{ (formData.heatingFee || 0).toFixed(2) }} 元</span>
          </div>
        </div>
        <el-form-item label="租金总计" prop="total">
          <el-input
            v-model.number="formData.total"
            placeholder="系统自动计算，可手动修改"
            type="number"
            min="0"
            step="0.01"
            @change="calcPaymentAmounts"
          ></el-input>
        </el-form-item>
        <el-form-item label="租金标准（元/㎡/天）含税" prop="rentRate">
          <el-input
            v-model="formData.rentRate"
            placeholder="请输入租金标准（保留两位小数）"
            type="number"
            min="0"
            step="0.01"
          ></el-input>
        </el-form-item>
      </template>
      <template v-if="activeStep === 5">
        <el-form-item label="政策来源" prop="policySource">
          <el-input
            v-model="formData.policySource"
            placeholder="请输入政策来源（非必填）"
            maxlength="200"
            show-word-limit
          ></el-input>
        </el-form-item>
        <el-form-item label="政策信息" prop="policyInfo">
          <el-input
            v-model="formData.policyInfo"
            placeholder="请输入政策信息（非必填）"
            type="textarea"
            :rows="5"
            maxlength="500"
            show-word-limit
          ></el-input>
        </el-form-item>
      </template>
    </el-form>

    <!-- 弹窗底部按钮 -->
    <div slot="footer" class="dialog-footer">
      <el-button @click="dialogVisible = false">取消</el-button>
      <template v-if="activeStep === 0">
        <el-button
          type="primary"
          @click="goToNextStep"
          :disabled="!formData.templateType"
        >
          下一步
        </el-button>
      </template>
      <template v-if="activeStep >= 1 && activeStep <= 4">
        <el-button @click="goToPrevStep">上一步</el-button>
        <el-button
          type="primary"
          @click="goToNextStep"
          :loading="isNavigating"
          :disabled="isNavigating"
        >
          下一步
        </el-button>
      </template>
      <template v-if="activeStep === 5">
        <el-button @click="goToPrevStep">上一步</el-button>
        <el-button
          type="primary"
          @click="submitContractForm"
          :loading="isSaving"
        >
          <template v-if="!isSaving">提交生成Word</template>
          <template v-else>提交中...</template>
        </el-button>
      </template>
    </div>
  </el-dialog>
</template>

<script>
import {
  Dialog,
  Form,
  FormItem,
  Input,
  Select,
  Option,
  DatePicker,
  Button,
  Steps,
  Step,
  Card,
  Divider,
  Table,
  TableColumn,
  // Descriptions,
  // DescriptionsItem,
} from "element-ui";
import {
  queryWordByContract,
  getStaffInfos,
  queryRooms,
  queryRoomAmounts,
} from "@/api/contract/newContractManageApi";
import { getCommunityId } from "@/api/community/communityApi";

export default {
  name: "ContractModal",
  components: {
    ElDialog: Dialog,
    ElForm: Form,
    ElFormItem: FormItem,
    ElInput: Input,
    ElSelect: Select,
    ElOption: Option,
    ElDatePicker: DatePicker,
    ElButton: Button,
    ElSteps: Steps,
    ElStep: Step,
    ElCard: Card,
    ElDivider: Divider,
    ElTable: Table,
    ElTableColumn: TableColumn,
    // ElDescriptions: Descriptions,
    // ElDescriptionsItem: DescriptionsItem,
  },
  data() {
    return {
      paymentDebounceTimer: null,
      isNavigating: false,
      dialogVisible: false,
      dialogTitle: "新增合同信息",
      isSaving: false,
      activeStep: 0,
      loadingStaff: false,
      loadingRooms: false,
      loadingRoomAmounts: false,
      staffList: [],
      roomList: [], // 所有房屋列表数据
      filteredRoomList: [], // 用于下拉显示的过滤后列表
      searchQuery: "", // 当前搜索关键词
      roomMap: {},
      roomAmountMap: {},
      totalRoomAmount: 0,
      communityId: "", // 社区ID
      isFirstEnterStep1: true,
      houseAmountUpdated: false,
      forceRefreshRoom: false, //强制刷新房屋列表标识
      formData: {
        templateType: "", // 模板类型（字符串）
        contractManager: "",
        selectedHouse: [], // 房屋选择（数组类型）
        // --- 甲方信息字段已移除 ---
        // partyAId: "",
        // partyAName: "",
        // partyAContact: "",
        // partyAPhone: "",
        // partyAEmail: "",
        // partyAmAilingAddress: "",
        // partyALegal: "",
        // partyAAddress: "",
        // 乙方信息
        partyBName: "",
        partyBAddress: "",
        partyBContact: "",
        partyBPhone: "",
        partyBEmail: "",
        partyBmAilingAddress: "", // 非必填
        partyBLegal: "",
        // 房屋与合同信息
        rent: "",
        houseAddress: "",
        signDate: "",
        houseArea: 0,
        buildingUsage: "",
        decorationAmount: "",
        investmentAmount: "",
        depositAmount: "",
        // 租赁与付款信息
        marginDeposit: "",
        incubationPeriod: "",
        incubationPeriodTimeRange: [], // [start_date, end_date]
        incubationPeriodTime: "", // 用于提交的文本格式
        houseDecorationPeriodRange: [], // [start_date, end_date]
        houseDecorationPeriod: "", // 用于提交的文本格式
        leaseTermRange: [], // [start_date, end_date]
        leaseTerm: "", // 用于提交的文本格式
        rentStart: "",
        rentEnd: "", // 新增字段
        paymentMethod: "", // 租赁支付方式: monthly, quarterly, half-yearly, yearly
        policySource: "", // 政策来源
        policyInfo: "", // 政策信息
        payments: [
          {
            paymentDate: "",
            startDate: "",
            endDate: "",
            rentalDays: "",
            rentAmount: "",
          },
        ],
        total: 0,
        rentRate: "",
      },
      formRules: {
        templateType: [
          { required: true, message: "请先选择合同模板", trigger: "change" },
        ],
        heatingFee: [
          {
            required: true,
            message: "请输入采暖费金额",
            trigger: "blur",
            validator: (rule, value, callback) => {
              // 仅特定模板校验必填
              if (
                !["houseRent", "houseRentNotHeating"].includes(
                  this.formData.templateType,
                )
              ) {
                callback();
                return;
              }
              const num = Number(value);
              if (isNaN(num) || num < 0) {
                callback(new Error("采暖费必须为非负数"));
              } else if (!/^\d+(\.\d{1,2})?$/.test(value.toString())) {
                callback(new Error("采暖费最多保留两位小数"));
              } else {
                callback();
              }
            },
          },
        ],
        contractManager: [
          { required: true, message: "请选择合同负责人", trigger: "change" },
        ],
        selectedHouse: [
          { required: true, message: "请至少选择一个房屋", trigger: "change" },
          {
            type: "array",
            min: 1,
            message: "至少选择一个房屋",
            trigger: "change",
          },
        ],
        // --- 甲方信息规则已移除 ---
        // partyAId: [
        //   { required: true, message: "甲方信息获取失败", trigger: "change" },
        // ],
        // 乙方信息规则
        partyBName: [
          { required: true, message: "请输入乙方公司全称", trigger: "blur" },
          { max: 100, message: "公司全称不能超过100个字符", trigger: "blur" },
        ],
        partyBAddress: [
          { required: true, message: "请输入乙方注册地址", trigger: "blur" },
          { max: 200, message: "注册地址不能超过200个字符", trigger: "blur" },
        ],
        partyBContact: [
          { required: true, message: "请输入乙方联系人姓名", trigger: "blur" },
          {
            max: 20,
            message: "乙方联系人姓名不能超过20个字符",
            trigger: "blur",
          },
        ],
        partyBPhone: [
          { required: true, message: "请输入乙方联系电话", trigger: "blur" },
          {
            validator: (rule, value, callback) => {
              const phoneRegex = /^1[3-9]\d{9}$/;
              const telRegex = /^0\d{2,3}-\d{7,8}$/;
              if (
                !value ||
                (!phoneRegex.test(value) && !telRegex.test(value))
              ) {
                callback(new Error("请输入正确的手机号或固定电话"));
              } else {
                callback();
              }
            },
            trigger: "blur",
          },
        ],
        // partyBEmail: [
        //   { required: true, message: "请输入乙方邮箱地址", trigger: "blur" },
        //   { type: "email", message: "请输入正确的邮箱格式", trigger: "blur" },
        // ],
        // partyBmAilingAddress 规则移除 required
        partyBLegal: [
          {
            required: true,
            message: "请输入乙方法定代表人姓名",
            trigger: "blur",
          },
          {
            max: 20,
            message: "法定代表人姓名不能超过20个字符",
            trigger: "blur",
          },
        ],
        rent: [
          {
            required: true,
            message: "请输入租金（中文大写）",
            trigger: "blur",
          },
          {
            pattern:
              /^(零|壹|贰|叁|肆|伍|陆|柒|捌|玖|拾|佰|仟|万|亿|元|角|分|整)+$/,
            message: "请输入合法的中文大写金额（如：壹万元整）",
            trigger: "blur",
          },
        ],
        houseAddress: [
          { required: true, message: "请输入房屋详细地址", trigger: "blur" },
          { max: 200, message: "房屋地址不能超过200个字符", trigger: "blur" },
        ],
        signDate: [
          { required: true, message: "请选择合同签订日期", trigger: "change" },
        ],
        houseArea: [
          { required: true, message: "请输入房屋建筑面积", trigger: "blur" },
          {
            validator: (rule, value, callback) => {
              const num = Number(value);
              if (isNaN(num) || num < 0) {
                callback(new Error("建筑面积必须为非负数"));
              } else if (!/^\d+(\.\d{1,2})?$/.test(value.toString())) {
                callback(new Error("建筑面积最多保留两位小数"));
              } else {
                callback();
              }
            },
            trigger: "blur",
          },
        ],
        buildingUsage: [
          { required: true, message: "请选择房屋用途", trigger: "change" },
        ],
        decorationAmount: [
          {
            required: true,
            message: "请输入装修金额（万元）",
            trigger: "blur",
          },
          {
            validator: (rule, value, callback) => {
              const num = Number(value);
              if (isNaN(num) || num < 0) {
                callback(new Error("装修金额必须为非负数"));
              } else if (!/^\d+(\.\d{1,2})?$/.test(value.toString())) {
                callback(new Error("装修金额最多保留两位小数"));
              } else {
                callback();
              }
            },
            trigger: "blur",
          },
        ],
        investmentAmount: [
          {
            required: true,
            message: "请输入投入金额（万元）",
            trigger: "blur",
          },
          {
            validator: (rule, value, callback) => {
              const num = Number(value);
              if (isNaN(num) || num < 0) {
                callback(new Error("投入金额必须为非负数"));
              } else if (!/^\d+(\.\d{1,2})?$/.test(value.toString())) {
                callback(new Error("投入金额最多保留两位小数"));
              } else {
                callback();
              }
            },
            trigger: "blur",
          },
        ],
        depositAmount: [
          {
            required: true,
            message: "请输入保证金金额（万元）",
            trigger: "blur",
          },
          {
            validator: (rule, value, callback) => {
              const num = Number(value);
              if (isNaN(num) || num < 0) {
                callback(new Error("保证金金额必须为非负数"));
              } else {
                callback();
              }
            },
            trigger: "blur",
          },
        ],
        marginDeposit: [
          { required: true, message: "请输入保证金金额", trigger: "blur" },
          {
            validator: (rule, value, callback) => {
              const num = Number(value);
              if (isNaN(num) || num < 0) {
                callback(new Error("保证金必须为非负数"));
              } else {
                callback();
              }
            },
            trigger: "blur",
          },
        ],
        incubationPeriod: [
          { required: true, message: "请输入孵化周期（年）", trigger: "blur" },
          {
            validator: (rule, value, callback) => {
              const num = Number(value);
              if (!Number.isInteger(num) || num < 1) {
                callback(new Error("孵化周期必须为正整数"));
              } else {
                callback();
              }
            },
            trigger: "blur",
          },
        ],
        // 时间范围改为必填
        incubationPeriodTimeRange: [
          {
            required: true,
            message: "请选择孵化周期时间范围",
            trigger: "change",
          },
        ],
        // houseDecorationPeriodRange: [
        //   { required: true, message: "请选择房屋装修期", trigger: "change" },
        // ],
        leaseTermRange: [
          { required: true, message: "请选择租赁期限", trigger: "change" },
        ],
        rentStart: [
          {
            required: true,
            message: "请选择租金标准开始日期",
            trigger: "change",
          },
        ],
        rentEnd: [
          {
            required: true,
            message: "请选择租金标准截止日期",
            trigger: "change",
          },
        ],
        paymentMethod: [
          { required: true, message: "请选择租赁支付方式", trigger: "change" },
        ],
        paymentDate: [
          { required: true, message: "请选择付款日期", trigger: "change" },
        ],
        startDate: [
          { required: true, message: "请选择租赁起始日期", trigger: "change" },
        ],
        endDate: [
          { required: true, message: "请选择租赁截止日期", trigger: "change" },
        ],
        rentalDays: [
          { required: true, message: "请输入计租天数", trigger: "blur" },
          {
            validator: (rule, value, callback) => {
              const num = Number(value);
              if (!Number.isInteger(num) || num < 1) {
                callback(new Error("计租天数必须为正整数"));
              } else {
                callback();
              }
            },
            trigger: "blur",
          },
        ],
        rentAmount: [
          {
            validator: (rule, value, callback) => {
              const num = Number(value);
              if (isNaN(num) || num < 0) {
                callback(new Error("租金金额不能为负数"));
              } else {
                callback();
              }
            },
            trigger: "change",
          },
        ],
        total: [
          { required: true, message: "租金总计不能为空", trigger: "blur" },
          {
            validator: (rule, value, callback) => {
              const sum = this.formData.payments.reduce((acc, item) => {
                return acc + (Number(item.rentAmount) || 0);
              }, 0);
              if (Number(value) !== sum) {
                callback(new Error(`总计金额应等于所有付款金额之和（${sum}）`));
              } else {
                callback();
              }
            },
            trigger: "blur",
          },
        ],
        rentRate: [
          { required: true, message: "请输入租金标准", trigger: "blur" },
          {
            validator: (rule, value, callback) => {
              const num = Number(value);
              if (isNaN(num) || num < 0) {
                callback(new Error("租金标准不能为负数"));
              } else if (!/^\d+(\.\d{1,2})?$/.test(value.toString())) {
                callback(new Error("租金标准最多保留两位小数"));
              } else {
                callback();
              }
            },
            trigger: "blur",
          },
        ],
        policySource: [],
        policyInfo: [],
      },
    };
  },
  watch: {
    activeStep(newVal) {
      if (newVal === 1) {
        this.$nextTick(async () => {
          // 仅首次进入步骤1时初始化
          if (this.isFirstEnterStep1) {
            this.formData.selectedHouse = [];
            await this.loadStaffList();
            this.isFirstEnterStep1 = false;
          } else {
            // 切回步骤1时，若负责人列表为空则加载
            if (this.staffList.length === 0) {
              await this.loadStaffList();
            }
          }
          // 强制更新组件
          this.$forceUpdate();
        });
      }
      if (newVal === 4) {
        this.$nextTick(() => {
          this.calcPaymentAmounts();
        });
      }
    },
    "formData.templateType"() {
      this.formData.selectedHouse = [];
    },
    "formData.payments": {
      deep: true,
      handler() {
        this.calcTotalAmount();
        this.calcPaymentAmounts();
      },
    },
    "formData.total": {
      handler() {
        this.calcPaymentAmounts();
      },
    },
    // 监听房屋选择变化
    "formData.selectedHouse": {
      deep: true,
      async handler(newVal) {
        if (newVal.length === 0) {
          this.formData.houseAddress = "";
          this.formData.houseArea = 0;
          this.formData.rent = "";
          this.formData.total = 0;
          this.totalRoomAmount = 0;
          this.roomAmountMap = {};
          // 清空后也触发更新
          this.houseAmountUpdated = !this.houseAmountUpdated;
          return;
        }
        // 拼接房屋名称
        const houseNames = newVal.map((roomId) => {
          const room = this.roomMap[roomId];
          return room ? room.roomName : "未知房屋";
        });
        if (!this.formData.houseAddress) {
          this.formData.houseAddress = houseNames.join("、");
        }
        // 计算总面积
        const totalArea = newVal.reduce((sum, roomId) => {
          const room = this.roomMap[roomId];
          return sum + (room ? Number(room.builtUpArea) || 0 : 0);
        }, 0);
        this.formData.houseArea = Number(totalArea.toFixed(2));

        // 查询房屋金额
        await this.calcTotalRoomAmount(newVal);
      },
    },
  },
  methods: {
    open() {
      this.dialogVisible = true;
      this.isSaving = false;
      this.activeStep = 0;
      this.isFirstEnterStep1 = true;
      // 重置表单数据
      this.formData = {
        templateType: "",
        heatingFee: 0,
        contractManager: "",
        selectedHouse: [],
        partyBName: "",
        partyBAddress: "",
        partyBContact: "",
        partyBPhone: "",
        partyBEmail: "",
        partyBmAilingAddress: "", // 非必填
        partyBLegal: "",
        rent: "",
        houseAddress: "",
        signDate: "",
        houseArea: 0,
        buildingUsage: "",
        decorationAmount: "",
        investmentAmount: "",
        depositAmount: "",
        marginDeposit: "",
        incubationPeriod: "",
        incubationPeriodTimeRange: [], // [start_date, end_date]
        incubationPeriodTime: "", // 用于提交的文本格式
        houseDecorationPeriodRange: [], // [start_date, end_date]
        houseDecorationPeriod: "", // 用于提交的文本格式
        leaseTermRange: [], // [start_date, end_date]
        leaseTerm: "", // 用于提交的文本格式
        rentStart: "",
        rentEnd: "",
        paymentMethod: "", // 新增
        payments: [
          {
            paymentDate: "",
            startDate: "",
            endDate: "",
            rentalDays: "",
            rentAmount: "",
          },
        ],
        total: 0,
        rentRate: "",
      };
      this.$nextTick(() => {
        if (this.$refs.contractForm) {
          this.$refs.contractForm.resetFields();
        }
        this.calcPaymentAmounts();
      });
    },

    getTemplateDesc() {
      const descMap = {
        houseRent:
          "本模板适用于装修抵扣租金且不含采暖费的房屋租赁场景，需填写装修金额、投入金额等特殊字段，租金支付方式分多期（可添加）。",
        houseRentNew:
          "光谷地区最新房屋租赁协议模板，适用于工业用地和厂房租赁场景，包含甲乙双方邮寄地址字段，租金分多期（可添加）支付。",
        houseRentHeating:
          "包含采暖费条款的房屋租赁协议终版，适用于办公或商业用途的房屋租赁，租金分多期（可添加）支付。",
        houseRentNotHeating:
          "不含采暖费条款的房屋租赁协议终版，适用于办公或商业用途的房屋租赁，租金分多期（可添加）支付。",
      };
      return (
        descMap[this.formData.templateType] || "该模板适用于房屋租赁场景..."
      );
    },

    // 模板选择后加载房屋列表
    async handleTemplateChange() {
      this.formData.buildingUsage = "";
      // this.formData.partyAmAilingAddress = "";  // 因为甲方信息已移除，此字段不再需要清空
      if (this.formData.templateType !== "houseRent") {
        this.formData.decorationAmount = "";
        this.formData.investmentAmount = "";
        this.formData.depositAmount = "";
      }
      // 重置房屋选择状态并强制刷新
      this.formData.selectedHouse = [];
      this.roomList = [];
      this.filteredRoomList = [];
      this.roomMap = {};
      this.roomAmountMap = {};
      this.totalRoomAmount = 0;
      this.searchQuery = "";
      this.forceRefreshRoom = true;
      if (this.$refs.houseSelect) {
        this.$refs.houseSelect.clearValue();
      }
      // 加载房屋列表（仅此时调用）
      await this.loadRoomList();
    },

    // 加载负责人列表
    async loadStaffList() {
      if (this.staffList.length > 0) return;
      this.loadingStaff = true;
      try {
        const res = await getStaffInfos({ page: 1, row: 1000 });
        this.staffList = res.staffs || [];
        if (this.staffList.length === 0) {
          this.$message.warning("未查询到负责人数据");
        }
      } catch (error) {
        console.error("加载负责人失败：", error);
        this.$message.error("加载负责人失败，请重试");
        this.staffList = [];
      } finally {
        this.loadingStaff = false;
      }
    },

    // 加载房屋列表（增加重复请求拦截）
    // 加载房屋列表（增加重复请求拦截 + 过滤状态2001）
    async loadRoomList() {
      if (this.roomList.length > 0 && !this.forceRefreshRoom) {
        console.log("房屋列表已存在，跳过重复请求");
        // 确保filteredRoomList也有数据
        this.filteredRoomList = this.roomList;
        return Promise.resolve(this.roomList);
      }
      if (this.loadingRooms) return Promise.resolve();

      this.loadingRooms = true;
      this.roomList = [];
      this.filteredRoomList = [];
      try {
        if (!this.communityId) {
          this.$message.warning("社区ID未获取到，无法加载房屋数据");
          return Promise.reject(new Error("communityId为空"));
        }

        const res = await queryRooms({
          roomType: "1010301",
          roomSubType: "",
          flag: "",
          page: 1,
          row: 1000,
          communityId: this.communityId,
        });

        // ===== 核心修改：过滤掉 state 为 '2001' 或 2001 的房屋 =====
        let newRoomList = res.rooms || [];
        newRoomList = newRoomList.filter((room) => {
          // 兼容字符串和数字类型
          return String(room.state) !== "2001";
        });
        // ===========================================================

        this.roomList = newRoomList;
        this.filteredRoomList = newRoomList; // 初始加载时，显示全部
        console.log("房屋列表加载完成（已过滤状态2001）：", newRoomList);

        // 构建房屋映射
        const newRoomMap = newRoomList.reduce((map, room) => {
          map[room.roomId] = {
            roomId: room.roomId,
            roomName: room.apartmentName || room.roomName,
            builtUpArea: Number(room.roomArea || room.builtUpArea) || 0,
            ...room,
          };
          return map;
        }, {});
        this.roomMap = newRoomMap;

        this.forceRefreshRoom = false;
        this.$forceUpdate();
        return Promise.resolve(newRoomList);
      } catch (error) {
        console.error("加载房屋失败：", error);
        this.$message.error("加载房屋失败，请重试");
        return Promise.reject(error);
      } finally {
        this.loadingRooms = false;
      }
    },

    // 新增：当用户点击输入框时，展示全部列表
    onHouseSelectFocus() {
      // 只有在没有搜索词时才显示全部
      if (!this.searchQuery) {
        this.filteredRoomList = this.roomList;
      }
    },

    // 新增：远程搜索房屋列表
    async searchRoomList(query) {
      this.searchQuery = query;
      this.loadingRooms = true;
      try {
        // 如果没有搜索词，显示全部列表
        if (!query) {
          this.filteredRoomList = this.roomList;
          return;
        }

        // 构建新的过滤列表
        const lowerQuery = query.toLowerCase();
        this.filteredRoomList = this.roomList.filter((room) => {
          // 检查 roomName 或 roomId 是否包含关键词
          const roomName = (room.roomName || "").toLowerCase();
          const roomId = (room.roomId || "").toLowerCase();
          return roomName.includes(lowerQuery) || roomId.includes(lowerQuery);
        });
      } catch (error) {
        console.error("搜索房屋失败:", error);
        this.$message.error("搜索房屋失败");
        this.filteredRoomList = [];
      } finally {
        this.loadingRooms = false;
      }
    },

    // 中文大写转数字
    chineseToNumber(chinese) {
      if (!chinese || typeof chinese !== "string") return 0;
      const digitMap = {
        零: 0,
        壹: 1,
        贰: 2,
        叁: 3,
        肆: 4,
        伍: 5,
        陆: 6,
        柒: 7,
        捌: 8,
        玖: 9,
      };
      const unitMap = { 拾: 10, 佰: 100, 仟: 1000, 万: 10000, 亿: 100000000 };
      let result = 0;
      let temp = 0;
      let unit = 1;

      chinese = chinese.replace(/整$/, "");
      const parts = chinese.split(/[元角分]/);
      const yuanPart = parts[0] || "";
      const jiaoPart = parts[1] || "";
      const fenPart = parts[2] || "";

      // 处理元部分
      for (let i = yuanPart.length - 1; i >= 0; i--) {
        const char = yuanPart[i];
        if (digitMap[char] !== undefined) {
          temp += digitMap[char] * unit;
        } else if (unitMap[char] !== undefined) {
          if (unit > unitMap[char]) {
            temp *= unitMap[char];
          }
          unit = unitMap[char];
          if (unit >= 10000) {
            result += temp;
            temp = 0;
          }
        }
      }
      result += temp;

      // 处理角分
      if (jiaoPart) result += digitMap[jiaoPart] * 0.1;
      if (fenPart) result += digitMap[fenPart] * 0.01;

      return Number(result.toFixed(2));
    },

    handlePaymentMethodChange() {
      // 清除之前的定时器，避免重复触发
      if (this.paymentDebounceTimer) {
        clearTimeout(this.paymentDebounceTimer);
      }
      // 使用window.setTimeout（可省略window，浏览器默认全局）
      this.paymentDebounceTimer = setTimeout(() => {
        this.generatePayments();
        this.paymentDebounceTimer = null; // 重置定时器标识
      }, 100);
    },

    // 组件销毁时清除定时器（避免内存泄漏）
    beforeDestroy() {
      if (this.paymentDebounceTimer) {
        clearTimeout(this.paymentDebounceTimer);
      }
    },

    // 数字转中文大写
    numberToChinese(num) {
      if (num === 0) return "零元整";
      const digits = [
        "零",
        "壹",
        "贰",
        "叁",
        "肆",
        "伍",
        "陆",
        "柒",
        "捌",
        "玖",
      ];
      const units = ["", "拾", "佰", "仟", "万", "拾", "佰", "仟", "亿"];
      const decimalUnits = ["角", "分"];
      let str = "";

      const integerPart = Math.floor(num);
      const decimalPart = Math.round((num - integerPart) * 100);

      // 处理整数部分
      if (integerPart > 0) {
        const integerStr = integerPart.toString();
        const length = integerStr.length;
        for (let i = 0; i < length; i++) {
          const digit = parseInt(integerStr[i]);
          const position = length - 1 - i;
          const unit = units[position];

          if (digit === 0) {
            if (i === 0 || integerStr[i - 1] !== "0") {
              str += digits[digit];
            }
          } else {
            str += digits[digit] + unit;
          }
        }
        str += "元";
      }

      // 处理小数部分
      if (decimalPart > 0) {
        const jiao = Math.floor(decimalPart / 10);
        const fen = decimalPart % 10;
        if (jiao > 0) str += digits[jiao] + decimalUnits[0];
        if (fen > 0) str += digits[fen] + decimalUnits[1];
      } else {
        str += "整";
      }

      if (str.startsWith("零元")) {
        str = str.replace("零元", "");
      }
      return str;
    },

    // 房屋选择变化回调
    handleHouseChange() {
      console.log("选中的房屋ID：", this.formData.selectedHouse);
    },

    // 获取已选房屋详情
    getSelectedHouseDetails() {
      return this.formData.selectedHouse.map((roomId) => {
        const room = this.roomMap[roomId] || {};
        // 从 roomAmountMap 中分别取出租金和采暖费
        const { rentAmount = 0, heatingAmount = 0 } =
          this.roomAmountMap[roomId] || {};
        return {
          roomName: room.roomName || "未知房屋",
          builtUpArea: room.builtUpArea || 0,
          roomSubTypeName: room.roomSubTypeName || "未知类型",
          stateName: room.stateName || "未知状态",
          rentAmount: rentAmount, // 单套租金
          heatingAmount: heatingAmount, // 单套采暖费
        };
      });
    },

    // 添加付款信息
    addPayment() {
      this.formData.payments.push({
        paymentDate: "",
        startDate: "",
        endDate: "",
        rentalDays: "",
        rentAmount: "",
      });
      this.$nextTick(() => {
        this.calcPaymentAmounts();
      });
    },

    // 删除付款信息
    removePayment(index) {
      if (this.formData.payments.length <= 1) {
        this.$message.warning("最少需保留1组付款信息");
        return;
      }
      this.formData.payments.splice(index, 1);
      this.calcTotalAmount();
      this.calcPaymentAmounts();
    },

    // 计算总金额
    calcTotalAmount() {
      const sum = this.formData.payments.reduce((acc, item) => {
        return acc + (Number(item.rentAmount) || 0);
      }, 0);
      this.formData.total = Number(sum.toFixed(2));
    },

    // 下一步
    goToNextStep() {
      if (this.isNavigating) return;
      this.isNavigating = true;
      this.validateAndNext();
    },

    // 验证并下一步
    validateAndNext() {
      const validateFields = this.getCurStepValidateFields();
      if (!Array.isArray(validateFields) || validateFields.length === 0) {
        this.activeStep++;
        this.isNavigating = false;
        return;
      }
      if (!this.$refs.contractForm) {
        this.isNavigating = false;
        return;
      }

      let completedCount = 0;
      const allErrors = {};

      const checkComplete = () => {
        completedCount++;
        if (completedCount === validateFields.length) {
          this.isNavigating = false;
          const hasError = Object.values(allErrors).some((err) => err);
          if (!hasError) {
            this.activeStep++;
          } else {
            this.$message.warning("请完善当前步骤的必填项并修正错误格式");
          }
        }
      };

      validateFields.forEach((field) => {
        this.$refs.contractForm.validateField(field, (errorMessage) => {
          allErrors[field] = errorMessage;
          checkComplete();
        });
      });
    },

    // 上一步
    goToPrevStep() {
      this.activeStep--;
    },

    // 获取当前步骤的校验字段
    getCurStepValidateFields() {
      switch (this.activeStep) {
        case 0:
          return ["templateType"];
        case 1:
          return ["contractManager", "selectedHouse"];
        // --- 步骤2 现在是填写乙方信息 ---
        case 2:
          return [
            "partyBName",
            "partyBAddress",
            "partyBContact",
            "partyBPhone",
            "partyBEmail",
            // "partyBmAilingAddress", // 非必填，移除
            "partyBLegal",
          ];
        // --- 步骤3 现在是房屋与合同信息 ---
        case 3: {
          const baseFields = [
            "rent",
            "houseAddress",
            "signDate",
            "houseArea",
            "buildingUsage",
          ];
          // 特定模板新增采暖费校验
          if (
            ["houseRent", "houseRentNotHeating"].includes(
              this.formData.templateType,
            )
          ) {
            baseFields.push("heatingFee");
          }
          // houseRent模板保留原有装修金额等字段
          if (this.formData.templateType === "houseRent") {
            baseFields.push(
              "decorationAmount",
              "investmentAmount",
              "depositAmount",
            );
          }
          return baseFields;
        }
        case 4: {
          const baseFields = [
            "marginDeposit",
            "incubationPeriod",
            "incubationPeriodTimeRange", // 改为范围选择器
            "houseDecorationPeriodRange", // 改为范围选择器
            "leaseTermRange", // 改为范围选择器
            "rentStart",
            "rentEnd",
            "paymentMethod", // 新增
            "total",
            "rentRate",
          ];
          const paymentFields = this.formData.payments
            .map((_, index) => [
              `payments[${index}].paymentDate`,
              `payments[${index}].startDate`,
              `payments[${index}].endDate`,
              `payments[${index}].rentalDays`,
              `payments[${index}].rentAmount`,
            ])
            .flat();

          return [...baseFields, ...paymentFields];
        }
        default:
          return [];
      }
    },

    // 关闭弹窗确认
    handleDialogClose(done) {
      this.$confirm("确定要取消吗？已填写的内容将不会保存。")
        .then(() => done())
        .catch(() => {});
    },

    // 孵化周期改变时触发，用于自动生成付款信息
    handleIncubationPeriodChange() {
      if (this.formData.paymentMethod && this.formData.rentStart) {
        this.generatePayments();
      }
    },

    // 租金标准开始日期改变时触发，用于自动生成付款信息
    handleRentStartDateChange() {
      if (this.formData.paymentMethod && this.formData.incubationPeriod) {
        this.generatePayments();
      }
    },

    // 新增：租金标准截止日期变化回调
    handleRentEndDateChange() {
      if (this.formData.paymentMethod && this.formData.rentStart) {
        this.generatePayments();
      }
    },

    // 核心修改：generatePayments 方法改为由开始/截止日期控制
    generatePayments() {
      if (
        !this.formData.paymentMethod ||
        !this.formData.rentStart ||
        !this.formData.rentEnd
      ) {
        this.$message.warning("请先选择租金标准开始日期和截止日期");
        return;
      }

      // 改为从租金标准开始/截止日期计算总时长
      const startDate = new Date(this.formData.rentStart);
      const endDate = new Date(this.formData.rentEnd);

      // 校验日期合法性
      if (endDate <= startDate) {
        this.$message.error("租金标准截止日期必须晚于开始日期");
        return;
      }

      let intervalMonths;
      switch (this.formData.paymentMethod) {
        case "monthly":
          intervalMonths = 1;
          break;
        case "quarterly":
          intervalMonths = 3;
          break;
        case "half-yearly":
          intervalMonths = 6;
          break;
        case "yearly":
          intervalMonths = 12;
          break;
        default:
          return;
      }

      this.formData.payments = [];
      let currentStart = new Date(startDate);
      let currentEnd = new Date(currentStart);

      // 循环生成每笔付款信息，直到超过截止日期
      while (currentStart <= endDate) {
        // 计算当期结束日期（按支付周期）
        currentEnd = new Date(currentStart);
        currentEnd.setMonth(currentEnd.getMonth() + intervalMonths);

        // 如果当期结束日期超过总截止日期，则以总截止日期为准
        if (currentEnd > endDate) {
          currentEnd = new Date(endDate);
        } else {
          // 否则取当期最后一天
          currentEnd.setDate(currentEnd.getDate() - 1);
        }

        // 计算计租天数（包含首尾）
        const days =
          Math.ceil(
            (currentEnd.getTime() - currentStart.getTime()) /
              (1000 * 60 * 60 * 24),
          ) + 1;

        this.formData.payments.push({
          paymentDate: this.formatDateForPicker(currentStart), // 付款日期为当期第一天
          startDate: this.formatDateForPicker(currentStart),
          endDate: this.formatDateForPicker(currentEnd),
          rentalDays: days,
          rentAmount: "",
        });

        // 更新下一期开始日期
        currentStart = new Date(currentEnd);
        currentStart.setDate(currentStart.getDate() + 1);
      }

      // 重新计算每笔金额
      this.$nextTick(() => {
        // 确保使用纯租金作为计算基数
        if (!this.formData.total && this.totalRoomAmount) {
          this.formData.total = Number(
            (this.totalRoomAmount + (this.formData.heatingFee || 0)).toFixed(2),
          );
        }
        this.calcPaymentAmounts(); // 调用修复后的方法
      });
    },

    // 新增方法：
    calcTotalWithHeatingFee() {
      // 仅特定模板计算总金额=租金+采暖费
      if (
        !["houseRent", "houseRentNotHeating"].includes(
          this.formData.templateType,
        )
      ) {
        return;
      }
      // 租金（total原本是纯租金） + 采暖费
      const rentAmount = Number(this.formData.total) || 0;
      const heatingAmount = Number(this.formData.heatingFee) || 0;
      // 总金额显示为租金+采暖费，但付款拆分仅用租金部分
      this.formData.total = Number((rentAmount + heatingAmount).toFixed(2));

      // 重新计算付款金额（仅按租金拆分）
      this.calcPaymentAmounts();
    },

    calcPaymentAmounts() {
      // 关键修改：明确区分租金和采暖费
      // 1. 从总金额中分离出纯租金，用于计算每笔付款
      const totalRent = Number(this.totalRoomAmount) || 0;
      const total = totalRent; // 付款金额只基于租金

      const count = this.formData.payments.length || 1;
      if (count === 0 || total <= 0) return;

      const amountPerGroup = total / count;
      this.formData.payments.forEach((payment) => {
        payment.rentAmount = Number(amountPerGroup.toFixed(2));
      });
    },

    async calcTotalRoomAmount(roomIds) {
      this.loadingRoomAmounts = true;
      try {
        const res = await queryRoomAmounts({ roomIds });
        const amountList = res.data || [];

        // 按 roomId 和 feeType 拆分金额
        this.roomAmountMap = amountList.reduce((map, item) => {
          if (!map[item.roomId]) {
            map[item.roomId] = { rentAmount: 0, heatingAmount: 0 };
          }
          if (item.feeType === "rent") {
            map[item.roomId].rentAmount = Number(item.amount) || 0;
          } else if (item.feeType === "heating") {
            map[item.roomId].heatingAmount = Number(item.amount) || 0;
          }
          return map;
        }, {});

        // 计算总租金和总采暖费
        let totalRent = 0;
        let totalHeating = 0;
        roomIds.forEach((roomId) => {
          const { rentAmount = 0, heatingAmount = 0 } =
            this.roomAmountMap[roomId] || {};
          totalRent += rentAmount;
          totalHeating += heatingAmount;
        });

        // 更新总租金（用于租金大写）
        this.totalRoomAmount = totalRent;
        this.formData.rent = this.numberToChinese(totalRent);

        // 特定模板下，总金额 = 租金 + 采暖费
        if (
          ["houseRent", "houseRentNotHeating"].includes(
            this.formData.templateType,
          )
        ) {
          this.formData.heatingFee = totalHeating;
          this.formData.total = Number((totalRent + totalHeating).toFixed(2));
        } else {
          this.formData.total = totalRent;
        }

        // ========== 核心修改开始 ==========
        // 1. 切换响应式标识，强制触发依赖更新
        this.houseAmountUpdated = !this.houseAmountUpdated;
        // 2. 先$nextTick确保数据更新，再手动触发组件刷新
        await this.$nextTick();
        // 3. 如果表格存在，手动触发重新渲染
        if (this.$refs.houseTable) {
          this.$refs.houseTable.doLayout(); // Element UI 表格重新布局
        }
        // ========== 核心修改结束 ==========

        // 重新计算付款金额
        this.calcPaymentAmounts();
        // 额外：如果已有支付方式，重新生成付款信息但保留金额
        if (this.formData.paymentMethod) {
          const tempTotal = this.formData.total;
          this.generatePayments();
          this.formData.total = tempTotal;
        }
      } catch (error) {
        console.error("查询房屋金额失败：", error);
        this.$message.error("获取房屋金额失败，请手动填写租金");
        this.totalRoomAmount = 0;
        this.roomAmountMap = {};
        this.formData.rent = "";
        this.formData.total = 0;
        this.formData.heatingFee = 0;
      } finally {
        this.loadingRoomAmounts = false;
      }
    },

    // 格式化日期用于日期选择器 (yyyy-MM-dd)
    formatDateForPicker(date) {
      if (!date) return null;
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },

    // 更新孵化周期时间范围文本
    updateIncubationPeriodText(dates) {
      if (dates && dates.length === 2) {
        this.formData.incubationPeriodTime = `${dates[0]}至${dates[1]}`;
      } else {
        this.formData.incubationPeriodTime = "";
      }
    },

    // 更新房屋装修期文本
    updateHouseDecorationPeriodText(dates) {
      if (dates && dates.length === 2) {
        this.formData.houseDecorationPeriod = `自${dates[0]}起至${dates[1]}`;
      } else {
        this.formData.houseDecorationPeriod = "";
      }
    },

    // 更新租赁期限文本
    updateLeaseTermText(dates) {
      if (dates && dates.length === 2) {
        const start = new Date(dates[0]);
        const end = new Date(dates[1]);
        const timeDiff = end.getTime() - start.getTime();
        const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1; // 包含首尾两天
        this.formData.leaseTerm = `自${dates[0]}起至${dates[1]}止，共计${daysDiff}天`;
      } else {
        this.formData.leaseTerm = "";
      }
    },

    // 提交生成Word
    async submitContractForm() {
      if (!this.$refs.contractForm) return;
      if (this.isSaving) return;

      this.$refs.contractForm.validate(async (valid) => {
        if (valid) {
          try {
            this.isSaving = true;
            const submitData = {
              communityId: this.communityId,
              templateCode: this.formData.templateType,
              contractManager: this.formData.contractManager,
              selectedHouse: this.formData.selectedHouse,
              heatingFee: this.formData.heatingFee, // 新增：采暖费
              partyBName: this.formData.partyBName,
              partyBAddress: this.formData.partyBAddress,
              partyBContact: this.formData.partyBContact,
              partyBPhone: this.formData.partyBPhone,
              partyBEmail: this.formData.partyBEmail,
              partyBmAilingAddress: this.formData.partyBmAilingAddress,
              partyBLegal: this.formData.partyBLegal,
              // 房屋与合同信息
              rent: this.formData.rent,
              houseAddress: this.formData.houseAddress,
              signDate: this.formatDateToChinese(this.formData.signDate),
              houseArea: this.formData.houseArea,
              buildingUsage: this.formData.buildingUsage,
              decorationAmount: this.formData.decorationAmount,
              investmentAmount: this.formData.investmentAmount,
              depositAmount: this.formData.depositAmount,
              // 租赁与付款信息
              marginDeposit: this.formData.marginDeposit,
              incubationPeriod: this.formData.incubationPeriod,
              incubationPeriodTime: this.formData.incubationPeriodTime, // 使用转换后的文本
              houseDecorationPeriod: this.formData.houseDecorationPeriod, // 使用转换后的文本
              leaseTerm: this.formData.leaseTerm, // 使用转换后的文本
              rentStart: this.formatDateToChinese(this.formData.rentStart),
              rentEnd: this.formatDateToChinese(this.formData.rentEnd),
              total: this.formData.total,
              rentRate: this.formData.rentRate,
              // 新增：政策信息字段（非必填）
              policySource: this.formData.policySource,
              policyInfo: this.formData.policyInfo,
              // 付款信息
              payments: this.formData.payments.map((payment, index) => ({
                paymentNo: index + 1,
                paymentDate: this.formatDateToChinese(payment.paymentDate),
                startDate: this.formatDateToChinese(payment.startDate),
                endDate: this.formatDateToChinese(payment.endDate),
                rentalDays: payment.rentalDays,
                rentAmount: payment.rentAmount,
              })),
            };

            const response = await queryWordByContract(submitData, {
              responseType: "json",
            });

            if (response.code !== 0) {
              throw new Error(`${response.msg || "服务器异常"}`);
            }
            if (!response.data) {
              throw new Error("未获取到Word文件数据");
            }

            // 下载文件
            const fileName = `${this.getTemplateName()}_${new Date().getTime()}.docx`;
            this.downloadFile(response.data, fileName);
            this.$message.success("合同Word文档生成成功，已开始下载！");
            this.dialogVisible = false;
            this.$emit("success");
          } catch (error) {
            console.error("合同生成失败：", error);
            this.$message.error(
              `操作失败：${error.message || "网络异常，请重试"}`,
            );
          } finally {
            this.isSaving = false;
          }
        } else {
          this.$message.warning("请完善所有必填项并修正错误格式");
        }
      });
    },

    // 下载文件
    downloadFile(base64Content, fileName) {
      try {
        let decodedBase64 = base64Content
          .trim()
          .replace(/-/g, "+")
          .replace(/_/g, "/");
        while (decodedBase64.length % 4) {
          decodedBase64 += "=";
        }

        const byteCharacters = atob(decodedBase64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], {
          type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document;charset=utf-8",
        });

        if (window.navigator.msSaveOrOpenBlob) {
          window.navigator.msSaveOrOpenBlob(blob, fileName);
        } else {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = fileName;
          a.style.display = "none";
          document.body.appendChild(a);
          a.click();
          setTimeout(() => {
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
          }, 500);
        }
      } catch (error) {
        console.error("文件下载失败：", error);
        this.$message.error(`下载失败：${error.message || "文件格式错误"}`);
      }
    },

    // 获取模板名称
    getTemplateName() {
      const templateMap = {
        houseRent: "房屋租赁协议（装修抵租金不含采暖费）",
        houseRentNew: "光谷房屋租赁协议最新版",
        houseRentHeating: "房屋租赁协议（含采暖费）",
        houseRentNotHeating: "房屋租赁协议（不含采暖费）",
      };
      return templateMap[this.formData.templateType] || "房屋租赁协议";
    },

    // 日期格式转换
    formatDateToChinese(dateStr) {
      if (!dateStr) return "";
      const [year, month, day] = dateStr.split("-");
      return `${year}年${month}月${day}日`;
    },

    // 租金变化回调
    handleRentChange() {
      if (this.formData.rent) {
        this.formData.total = this.chineseToNumber(this.formData.rent);
      }
    },
  },
  async created() {
    // 异步获取社区ID
    this.communityId = await getCommunityId();
    // 预加载负责人列表
    this.loadStaffList();
  },
};
</script>

<style scoped>
.contract-form {
  margin-top: 15px;
}

.dialog-footer {
  text-align: right;
  margin-top: 20px;
}

.el-form-item {
  margin-bottom: 16px;
}

.required-item .el-form-item__label::before {
  content: "*";
  color: #ff4d4f;
  margin-right: 4px;
}

.template-desc {
  margin-top: 20px;
  background: #f5fafe;
  border: 1px solid #e8f4f8;
}

.template-tips {
  margin-top: 10px;
  padding: 8px 12px;
  background: #e8f4f8;
  border-radius: 4px;
  color: #4299e1;
  font-size: 13px;
}

.template-tips .el-icon-info {
  margin-right: 6px;
}

.el-divider {
  margin: 20px 0;
}

.el-divider__text {
  color: #4299e1;
  font-weight: 500;
}

.payment-group {
  margin-bottom: 25px;
  padding: 15px;
  background: #fafafa;
  border-radius: 8px;
}

.payment-group-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}
</style>
